import { useEffect } from 'react'
import {
  publishReadinessVerdict,
  type PublishReadinessGateLike,
} from '@/features/validation/publish-readiness-verdict'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * Publish the publish-readiness pre-flight estimate into the canonical verdict
 * store from a publishing rail (F25 / ST-5).
 *
 * BOTH the Publish rail AND the Deploy rail are real publishing entry points
 * (each mounts PublishModal and computes the same publishGate). The pre-flight
 * estimate must be published from whichever rail the creator visits — wiring it
 * to only one rail left the app-health pill with no readiness vote on the other
 * (ST-5). This hook is the single shared publisher both rails call so the
 * behavior is identical.
 *
 * `publishReadinessVerdict` WRITES ONLY ON CHANGE, so keying the effect on the
 * derived signature (publishReady + first reason) is enough to avoid an effect
 * loop even though `gate` is a fresh object every render. We only publish once a
 * project is open — a pre-flight for "no project" is meaningless.
 */
export function usePublishReadinessPublish(
  currentProjectId: string | null,
  gate: PublishReadinessGateLike,
  locale: LocaleCode,
): void {
  const firstReason = gate.reasons[0] ?? null
  useEffect(() => {
    if (!currentProjectId) return
    publishReadinessVerdict(gate, locale)
    // `gate` is re-derived (a fresh object) every render; the primitive
    // signature fields below are the real inputs, and the helper's change-guard
    // + the sticky server-blocked flag are the loop/clobber backstops — so
    // depending on `gate` itself would re-fire every render, which is exactly
    // what we must avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId, gate.publishReady, firstReason, locale])
}
